import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { Button, Page, PageContentHeader, Spinner, TableList } from 'seagull';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import TableFilters from '../../../components/TableFilters/TableFilters';
import FutureInvoiceRow from './FutureInvoiceRow';
import styles from './FutureInvoices.module.scss';
import { futureInvoicesReportColumnDefs, futureInvoicesReportTextsPath, getDefaultSort, getHeaderItems, makeCsv, makeFilterConfig } from './FutureInvoices.utils';
import { FilterType, filterLabels } from './types';
const testIdPrefix = 'future-invoices-report';
export const FutureInvoicesReport = ()=>{
    const history = useHistory();
    const [futureInvoicesReportData, setFutureInvoicesReportData] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const { getNow, browser: { downloadCsv } } = useIoContext();
    const { t: reportTexts } = useLocaleKeys(futureInvoicesReportTextsPath);
    const { t: reportsTexts } = useLocaleKeys('pages.reports');
    const title = reportsTexts.futureInvoicesTitle();
    const headerItems = getHeaderItems(reportTexts.columnHeaders);
    const sortIds = compact(headerItems.map((item)=>item.sortId));
    const defaultSort = useMemo(()=>getDefaultSort(sortIds), [
        sortIds
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const handleFetchFilterConfig = async ()=>{
        const [companyNames, agreementNames] = await Promise.all([
            api.reports.getFutureInvoicesCompanyNames(),
            api.reports.getFutureInvoicesAgreementNames()
        ]);
        const filterOptions = {
            [FilterType.CompanyName]: companyNames,
            [FilterType.AgreementName]: agreementNames
        };
        return makeFilterConfig({
            filtersOptions: filterOptions,
            now: getNow(),
            texts: reportTexts.dateValidation
        });
    };
    const fetchFutureInvoicesReport = (selectedFilters, sort)=>{
        var _selectedFilters_FilterType_IssueDate, _selectedFilters_FilterType_IssueDate1, _selectedFilters_FilterType_DueDate, _selectedFilters_FilterType_DueDate1;
        if (businessId == null) return;
        setIsLoading(true);
        api.reports.getFutureInvoicesReport({
            companies: selectedFilters[FilterType.CompanyName],
            agreements: selectedFilters[FilterType.AgreementName],
            requiresApproval: selectedFilters[FilterType.RequiresApproval],
            issueDateFrom: (_selectedFilters_FilterType_IssueDate = selectedFilters[FilterType.IssueDate]) === null || _selectedFilters_FilterType_IssueDate === void 0 ? void 0 : _selectedFilters_FilterType_IssueDate.from,
            issueDateTo: (_selectedFilters_FilterType_IssueDate1 = selectedFilters[FilterType.IssueDate]) === null || _selectedFilters_FilterType_IssueDate1 === void 0 ? void 0 : _selectedFilters_FilterType_IssueDate1.to,
            dueDateFrom: (_selectedFilters_FilterType_DueDate = selectedFilters[FilterType.DueDate]) === null || _selectedFilters_FilterType_DueDate === void 0 ? void 0 : _selectedFilters_FilterType_DueDate.from,
            dueDateTo: (_selectedFilters_FilterType_DueDate1 = selectedFilters[FilterType.DueDate]) === null || _selectedFilters_FilterType_DueDate1 === void 0 ? void 0 : _selectedFilters_FilterType_DueDate1.to,
            sortField: sort ? sortIds[sort.colIndex] : undefined,
            sortDirection: sort === null || sort === void 0 ? void 0 : sort.sort
        }).then(setFutureInvoicesReportData).finally(()=>setIsLoading(false));
    };
    useSetDocumentTitle(title);
    useOnMount(()=>{
        fetchFutureInvoicesReport(selectedFilters, sort);
    });
    const onExport = ()=>{
        downloadCsv(makeCsv({
            futureInvoices: futureInvoicesReportData,
            texts: reportTexts
        }), 'future-invoices.csv');
    };
    const onSortChange = (sort)=>{
        changeSort(sort);
        fetchFutureInvoicesReport(selectedFilters, sort);
    };
    return /*#__PURE__*/ _jsx(Page, {
        testId: testIdPrefix,
        header: title,
        variant: "containedHeight",
        onClickBack: ()=>history.push(routes.reports),
        actions: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'export_future_invoices_report'
            },
            testId: "".concat(testIdPrefix, "-export-btn"),
            disabled: isLoading,
            onClick: onExport,
            children: "Export"
        }),
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            columnDefs: futureInvoicesReportColumnDefs,
            numRows: futureInvoicesReportData.length,
            sort: sort,
            onSortChange: onSortChange,
            children: [
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.pageContent,
                        children: /*#__PURE__*/ _jsx(RestfulWrapper, {
                            fetch: handleFetchFilterConfig,
                            renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                                    size: 24
                                }),
                            renderChild: (param)=>{
                                let { data: filterConfig } = param;
                                return /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: testIdPrefix,
                                    filterLabels: filterLabels,
                                    filterConfig: filterConfig,
                                    selectedFilters: selectedFilters,
                                    setSelectedFilters: setSelectedFilters,
                                    onRefresh: fetchFutureInvoicesReport
                                });
                            }
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(TableList.Structure, {
                    header: /*#__PURE__*/ _jsx(TableList.Header, {
                        stickyToTop: true,
                        items: headerItems,
                        testId: "".concat(testIdPrefix, "-table-header")
                    }),
                    content: isLoading ? /*#__PURE__*/ _jsx(Spinner, {
                        centeredInBox: true
                    }) : /*#__PURE__*/ _jsx(_Fragment, {
                        children: futureInvoicesReportData.map((futureInvoice, index)=>/*#__PURE__*/ _jsx(FutureInvoiceRow, {
                                futureInvoice: futureInvoice,
                                testId: "".concat(testIdPrefix, "-table-row-").concat(index)
                            }, futureInvoice.compoundId()))
                    })
                })
            ]
        })
    });
};
